import { Link } from "react-router-dom";
import { GraduationCap, Award, Briefcase, Stethoscope } from "lucide-react";
import { DOCTOR } from "@/config/doctorConfig";
import { SectionHeading, Reveal } from "@/components/motion";

const credentials = [
  { icon: GraduationCap, label: "MBBS", detail: "Bachelor of Medicine, Bachelor of Surgery" },
  { icon: Award, label: "MD (Internal Medicine)", detail: "Postgraduate specialist in adult medicine" },
  { icon: Stethoscope, label: "CCEBDM", detail: "Certificate Course in Evidence-Based Diabetes Management" },
  { icon: Briefcase, label: "Assistant Professor", detail: "Academic & teaching role in medicine" },
];

export const About = () => (
  <section id="about" className="bg-background py-16 sm:py-24 lg:py-32" data-testid="about-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-5 order-2 lg:order-1">
          <Reveal>
            <figure className="relative max-w-md mx-auto lg:mx-0">
              <div aria-hidden="true" className="absolute -top-4 -left-4 w-full h-full rounded-[1.75rem] border-2 border-teal-600/20" />
              <img
                src={DOCTOR.photoOffice}
                alt="Dr. Suyash Bhadoriya at his clinic — Physician in Saket Nagar, Bhopal"
                loading="lazy"
                className="relative w-full rounded-[1.75rem] shadow-frame object-cover"
                data-testid="about-doctor-photo"
              />
              <figcaption className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur rounded-xl px-4 py-3 shadow-soft">
                <p className="font-serif font-semibold text-navy-900 text-sm">{DOCTOR.name}</p>
                <p className="text-[11px] font-mono tracking-wide text-teal-700 uppercase mt-0.5">{DOCTOR.qualifications}</p>
              </figcaption>
            </figure>
          </Reveal>
        </div>

        <div className="lg:col-span-7 order-1 lg:order-2">
          <SectionHeading
            number="01"
            eyebrow="About"
            title="About Dr. Suyash Bhadoriya"
          />
          <Reveal delay={2}>
            <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed">
              Dr. Suyash Bhadoriya is an MBBS, MD (Internal Medicine) physician based in Bhopal,
              Madhya Pradesh. He provides comprehensive medical evaluation and management for
              adults, with a focus on Internal Medicine, diabetes care, hypertension and other
              common and complex medical conditions.
            </p>
            <p className="mt-4 text-base text-slate-600 leading-relaxed">
              As an MD (Internal Medicine) physician in Bhopal, he offers comprehensive medical
              consultation and management for adults — combining careful clinical evaluation with
              evidence-based treatment. He serves as an Assistant Professor and practises as a
              General Physician &amp; Internal Medicine Specialist at {DOCTOR.hospitalName}, {DOCTOR.addressLine1}, Bhopal.
            </p>
            <p className="mt-4 text-base text-slate-600 leading-relaxed">
              Looking for a{" "}
              <Link to="/general-physician-bhopal" className="text-teal-700 font-semibold hover:text-teal-900 underline underline-offset-4 decoration-teal-300 transition-colors">
                General Physician in Bhopal
              </Link>
              ? Learn about consultation scope, OPD timings and clinic directions on the dedicated page.
            </p>
          </Reveal>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="credentials-grid">
            {credentials.map((c, i) => (
              <Reveal key={c.label} delay={i}>
                <div className="flex gap-3.5 items-start bg-white rounded-xl border border-slate-200/80 shadow-soft p-4">
                  <div className="shrink-0 w-10 h-10 rounded-lg bg-navy-900 flex items-center justify-center">
                    <c.icon size={18} className="text-teal-300" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">{c.label}</p>
                    <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{c.detail}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);
